import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { AdminField } from "./shared";

export default function BookingEditDialog({ booking, onClose, onSaved }) {
    const [cabins, setCabins] = useState([]);
    const [experiences, setExperiences] = useState([]);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        guest_name: booking.guest_name || "",
        guest_email: booking.guest_email || "",
        guest_phone: booking.guest_phone || "",
        cabin_id: booking.cabin_id,
        check_in: booking.check_in,
        check_out: booking.check_out,
        guests: booking.guests || 2,
        notes: booking.notes || "",
        experiences: (booking.experiences || []).reduce((acc, e) => { acc[e.experience_id] = e.quantity; return acc; }, {}),
    });

    useEffect(() => {
        api.get("/cabins?active=").then((r) => setCabins(r.data));
        api.get("/experiences?active=").then((r) => setExperiences(r.data));
    }, []);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setExp = (id, qty) => setForm((f) => {
        const next = { ...f.experiences };
        if (qty <= 0) delete next[id]; else next[id] = qty;
        return { ...f, experiences: next };
    });

    const save = async () => {
        setSaving(true);
        try {
            const payload = {
                guest_name: form.guest_name,
                guest_email: form.guest_email,
                guest_phone: form.guest_phone,
                cabin_id: form.cabin_id,
                check_in: form.check_in,
                check_out: form.check_out,
                guests: Number(form.guests),
                notes: form.notes,
                experiences: Object.entries(form.experiences).map(([experience_id, quantity]) => ({ experience_id, quantity })),
            };
            await api.patch(`/bookings/${booking.id}`, payload);
            toast.success("Reservation updated");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Update failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-edit-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-3xl">Edit reservation</DialogTitle>
                    <DialogDescription>Change dates, cabin, guests or activities. The total is recalculated automatically.</DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <AdminField label="Guest name" value={form.guest_name} onChange={(v) => set("guest_name", v)} testid="edit-guest-name" />
                    <AdminField label="Guest email" value={form.guest_email} onChange={(v) => set("guest_email", v)} testid="edit-guest-email" />
                    <AdminField label="Phone" value={form.guest_phone} onChange={(v) => set("guest_phone", v)} testid="edit-guest-phone" />
                    <div>
                        <label className="eyebrow block mb-2">Cabin</label>
                        <select value={form.cabin_id} onChange={(e) => set("cabin_id", e.target.value)} data-testid="edit-cabin" className="w-full border border-espresso/20 px-3 py-2 text-sm bg-transparent focus:border-forest outline-none">
                            {cabins.map((c) => <option key={c.id} value={c.id}>{c.name} — ${c.price_per_night}/nt</option>)}
                        </select>
                    </div>
                    <AdminField label="Check-in (YYYY-MM-DD)" value={form.check_in} onChange={(v) => set("check_in", v)} testid="edit-checkin" />
                    <AdminField label="Check-out (YYYY-MM-DD)" value={form.check_out} onChange={(v) => set("check_out", v)} testid="edit-checkout" />
                    <AdminField label="Guests" type="number" value={form.guests} onChange={(v) => set("guests", v)} testid="edit-guests" />
                    <div className="md:col-span-2">
                        <label className="eyebrow block mb-2">Notes</label>
                        <textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="edit-notes" className="w-full border border-espresso/20 p-3 text-sm bg-transparent" />
                    </div>
                </div>

                <div className="mt-5">
                    <div className="eyebrow mb-3">Activities</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="edit-experiences">
                        {experiences.map((e) => {
                            const qty = form.experiences[e.id] || 0;
                            return (
                                <div key={e.id} className={`border ${qty > 0 ? "border-forest bg-forest/5" : "border-espresso/15"} px-3 py-2 flex items-center justify-between gap-2`}>
                                    <div className="text-sm">{e.name} <span className="text-muted2 text-xs">${e.price}</span></div>
                                    <div className="flex items-center gap-1.5">
                                        <button type="button" onClick={() => setExp(e.id, qty - 1)} disabled={qty <= 0} data-testid={`edit-exp-decr-${e.id}`} className="w-6 h-6 border border-espresso/30 inline-flex items-center justify-center disabled:opacity-30"><Minus size={11} /></button>
                                        <span className="w-6 text-center text-sm">{qty}</span>
                                        <button type="button" onClick={() => setExp(e.id, qty + 1)} data-testid={`edit-exp-incr-${e.id}`} className="w-6 h-6 border border-espresso/30 inline-flex items-center justify-center hover:bg-bone2"><Plus size={11} /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <DialogFooter className="mt-6">
                    <button onClick={onClose} className="px-5 py-3 border border-espresso/30 text-xs uppercase tracking-widest2">Cancel</button>
                    <button onClick={save} disabled={saving} data-testid="edit-save" className="px-5 py-3 bg-forest text-bone text-xs uppercase tracking-widest2 font-semibold disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
